import type { SearchResultItem } from '~/modules/search';
import type {
    ArchizeenTileFragment,
    ArticleThumbnailAllSizesFragment,
} from 'generated/graphql';
import type { BasicThumbnail } from '~/utils/get-tile-thumbnail-for-professional';
import { isBasicThumbnail } from '~/utils/get-tile-thumbnail-for-professional';
import type { FeatureTileType } from '~/modules/articles/type';

type articleWithThumbnail =
    | BasicThumbnail
    | ArticleThumbnailAllSizesFragment
    | ArchizeenTileFragment
    | SearchResultItem;

const isSearchResultItem = (p: articleWithThumbnail): p is SearchResultItem => {
    return (p as SearchResultItem).PType !== undefined;
};

const isArticleThumbnailAllSizes = (
    p: articleWithThumbnail,
): p is ArticleThumbnailAllSizesFragment => {
    return (
        (p as ArticleThumbnailAllSizesFragment).LandscapeThumbnail?.mobile !==
            undefined &&
        (p as ArticleThumbnailAllSizesFragment).SquareThumbnail?.desktop !==
            undefined
    );
};

const isArchizeenTileFragment = (
    p: articleWithThumbnail,
): p is ArchizeenTileFragment => {
    return (
        (p as ArchizeenTileFragment).LibraryThumb?.ThumbImageFile
            ?.DirectoryPageLandscape.Desktop !== undefined
    );
};

// we have many place need to maintain get the tile thumbnail from a article
// so we create this backward-compatible function to use in all places
// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1788313607
export const getArticleTileThumbnail = (
    article: articleWithThumbnail,
    thumbnailType: FeatureTileType,
    isMobile?: boolean,
): string => {
    if (isArticleThumbnailAllSizes(article)) {
        let thumbnailObj;
        switch (thumbnailType) {
            case 'main':
            case 'square':
                thumbnailObj = article.SquareThumbnail;
                break;
            case 'portrait':
                thumbnailObj = article.PortraitThumbnail;
                break;
            case 'landscape':
                thumbnailObj = article.LandscapeThumbnail;
                break;
            default:
                thumbnailObj = isMobile
                    ? article.SquareThumbnail
                    : article.LandscapeThumbnail;
                break;
        }

        const thumbnail = isMobile
            ? thumbnailObj?.mobile
            : thumbnailObj?.desktop;
        if (thumbnail) {
            return thumbnail;
        }
    }

    if (isBasicThumbnail(article)) {
        const thumbnail = isMobile
            ? article.Thumbnail?.mobile
            : article.Thumbnail?.desktop;

        if (
            article.Thumbnail &&
            'portrait' in article.Thumbnail &&
            thumbnailType === 'portrait'
        ) {
            return article.Thumbnail.portrait as string;
        }

        if (thumbnail) {
            return thumbnail;
        }
    }

    // check if article is a SearchResultItem
    if (isSearchResultItem(article)) {
        const thumbs = article.LibraryThumbs;
        return (
            thumbs?.ThumbPortrait ||
            thumbs?.Thumb_category ||
            thumbs?.Thumb_lg ||
            ''
        );
    }

    // check if article is a ArchizeenTileFragment
    if (isArchizeenTileFragment(article)) {
        const thumbnail =
            thumbnailType === 'main'
                ? article.LibraryThumb?.ThumbImageFile?.DirectoryPageFeatured
                      .Desktop
                : thumbnailType === 'landscape'
                  ? article.LibraryThumb?.ThumbImageFile?.DirectoryPageLandscape
                        .Desktop
                  : article.LibraryThumb?.ThumbImageFile?.DirectoryPageSquare
                        .Desktop;
        return thumbnail || '';
    }

    return '';
};
